import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { rupiah, formatDate } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Skeleton } from "@/components/ui/skeleton";
import { Loader2, Lock, ShieldCheck, FileBarChart, AlertTriangle, Info } from "lucide-react";

const BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PERIODS = [
  { value: "today", label: "Hari Ini" },
  { value: "week", label: "7 Hari" },
  { value: "month", label: "30 Hari" },
  { value: "year", label: "1 Tahun" },
];

function CenterWrap({ children }) {
  return <div className="min-h-screen grid place-items-center bg-background px-4">{children}</div>;
}

export default function SharedReport() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [viewToken, setViewToken] = useState(() => sessionStorage.getItem(`share_view_${token}`) || "");

  useEffect(() => {
    axios
      .get(`${BASE}/reports/shared/${token}`)
      .then((r) => setInfo(r.data))
      .catch((e) => setErr(e?.response?.data?.detail || "Link tidak valid"))
      .finally(() => setLoading(false));
  }, [token]);

  const onAuthed = (vt) => {
    sessionStorage.setItem(`share_view_${token}`, vt);
    setViewToken(vt);
  };
  const onExpire = () => {
    sessionStorage.removeItem(`share_view_${token}`);
    setViewToken("");
  };

  if (loading) return <CenterWrap><Loader2 className="h-6 w-6 animate-spin text-primary" /></CenterWrap>;
  if (err)
    return (
      <CenterWrap>
        <Card className="p-6 max-w-sm text-center space-y-2" data-testid="shared-error">
          <AlertTriangle className="h-8 w-8 text-amber-500 mx-auto" />
          <p className="font-semibold">Link tidak tersedia</p>
          <p className="text-sm text-muted-foreground">{err}</p>
        </Card>
      </CenterWrap>
    );

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b bg-card/60 backdrop-blur sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-4 py-3.5 flex items-center gap-3">
          <div className="h-9 w-9 rounded-lg bg-primary/10 grid place-items-center">
            <FileBarChart className="h-4 w-4 text-primary" />
          </div>
          <div className="min-w-0">
            <p className="font-bold text-sm leading-tight truncate">{info.business_name}</p>
            <p className="text-xs text-muted-foreground">Laporan · Tampilan Read-only</p>
          </div>
        </div>
      </header>
      <main className="max-w-5xl mx-auto px-4 py-5">
        {!viewToken ? (
          <PinGate token={token} pinSet={info.pin_set} label={info.label} onAuthed={onAuthed} />
        ) : (
          <ReadOnlyReports token={token} viewToken={viewToken} onExpire={onExpire} />
        )}
      </main>
      <footer className="max-w-5xl mx-auto px-4 py-8 flex flex-col items-center gap-2">
        <img
          src="/avatar.jpg"
          alt="Abdul Muis"
          className="w-24 h-24 rounded-full object-cover object-[center_35%] ring-2 ring-border shadow-md select-none pointer-events-none"
        />
        <div className="text-center leading-tight">
          <p className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground">Dibuat oleh</p>
          <p className="text-lg font-bold tracking-tight">Abdul Muis</p>
          <p className="text-xs text-muted-foreground">Pengembang Website</p>
        </div>
        <p className="text-xs text-muted-foreground mt-1">Tautan aman · hanya menampilkan laporan</p>
      </footer>
    </div>
  );
}

function PinGate({ token, pinSet, label, onAuthed }) {
  const [pin, setPin] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const creating = !pinSet;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!/^\d{4,8}$/.test(pin)) {
      setError("PIN harus 4-8 digit angka");
      return;
    }
    if (creating && pin !== confirm) {
      setError("Konfirmasi PIN tidak sama");
      return;
    }
    setBusy(true);
    try {
      const path = creating ? "set-pin" : "verify";
      const r = await axios.post(`${BASE}/reports/shared/${token}/${path}`, { pin });
      onAuthed(r.data.view_token);
    } catch (e2) {
      setError(e2?.response?.data?.detail || "Gagal memproses PIN");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grid place-items-center py-8">
      <Card className="p-6 w-full max-w-sm space-y-4">
        <div className="text-center space-y-1.5">
          <div className="h-12 w-12 rounded-xl bg-primary/10 grid place-items-center mx-auto">
            <Lock className="h-5 w-5 text-primary" />
          </div>
          <p className="font-bold text-lg">{creating ? "Buat PIN Akses" : "Masukkan PIN"}</p>
          <p className="text-sm text-muted-foreground">
            {creating
              ? `Halo ${label}, buat PIN untuk mengamankan akses laporan ini. Simpan PIN untuk masuk berikutnya.`
              : `Halo ${label}, masukkan PIN untuk melihat laporan.`}
          </p>
        </div>
        <form onSubmit={submit} className="space-y-3">
          <Input
            data-testid="pin-input"
            type="password"
            inputMode="numeric"
            placeholder="PIN (4-8 digit)"
            value={pin}
            onChange={(e) => setPin(e.target.value.replace(/\D/g, ""))}
            maxLength={8}
            autoFocus
          />
          {creating && (
            <Input
              data-testid="pin-confirm-input"
              type="password"
              inputMode="numeric"
              placeholder="Ulangi PIN"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value.replace(/\D/g, ""))}
              maxLength={8}
            />
          )}
          {error && <p className="text-sm text-rose-600" data-testid="pin-error">{error}</p>}
          <Button data-testid="pin-submit" type="submit" disabled={busy} className="w-full gap-2">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />}
            {creating ? "Simpan & Lihat Laporan" : "Buka Laporan"}
          </Button>
        </form>
      </Card>
    </div>
  );
}

function ReadOnlyReports({ token, viewToken, onExpire }) {
  const [tab, setTab] = useState("ringkasan");
  const authGet = useCallback(
    (path, params) =>
      axios
        .get(`${BASE}/reports/shared/${token}${path}`, { params, headers: { "X-View-Token": viewToken } })
        .catch((e) => {
          if (e?.response?.status === 401) onExpire();
          throw e;
        }),
    [token, viewToken, onExpire]
  );

  return (
    <Tabs value={tab} onValueChange={setTab}>
      <TabsList>
        <TabsTrigger data-testid="shared-tab-ringkasan" value="ringkasan">Ringkasan</TabsTrigger>
        <TabsTrigger data-testid="shared-tab-harian" value="harian">Rekap Harian</TabsTrigger>
        <TabsTrigger data-testid="shared-tab-rekapitulasi" value="rekapitulasi">Rekapitulasi</TabsTrigger>
      </TabsList>
      <TabsContent value="ringkasan" className="mt-4"><SharedRingkasan authGet={authGet} /></TabsContent>
      <TabsContent value="harian" className="mt-4"><SharedHarian authGet={authGet} /></TabsContent>
      <TabsContent value="rekapitulasi" className="mt-4"><SharedRekap authGet={authGet} /></TabsContent>
    </Tabs>
  );
}

function StatBox({ label, value, isNumber, tone, bold }) {
  const colors = { success: "text-emerald-600 dark:text-emerald-400", danger: "text-rose-600 dark:text-rose-400" };
  return (
    <Card className="p-4">
      <p className="text-xs uppercase tracking-wide text-muted-foreground font-medium">{label}</p>
      <p className={`mt-1.5 money ${bold ? "text-2xl font-bold" : "text-xl font-semibold"} tracking-tight ${colors[tone] || ""}`}>
        {isNumber ? value : rupiah(value)}
      </p>
    </Card>
  );
}

function SharedRingkasan({ authGet }) {
  const [period, setPeriod] = useState("today");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    authGet("/summary", { period })
      .then((r) => setData(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [period, authGet]);

  const s = data?.summary;
  return (
    <div className="space-y-4">
      <Tabs value={period} onValueChange={setPeriod}>
        <TabsList>
          {PERIODS.map((p) => (
            <TabsTrigger key={p.value} value={p.value} data-testid={`shared-period-${p.value}`}>{p.label}</TabsTrigger>
          ))}
        </TabsList>
      </Tabs>
      {loading || !s ? (
        <Skeleton className="h-52 w-full" />
      ) : (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <StatBox label="Jumlah Transaksi" value={s.jumlah_transaksi} isNumber />
          <StatBox label="Omzet" value={s.omzet} />
          <StatBox label="Keuntungan" value={s.keuntungan} tone="success" />
          <StatBox label="Biaya Admin" value={s.biaya_admin} />
          <StatBox label="Pengeluaran" value={s.pengeluaran} tone="danger" />
          <StatBox label="Laba Bersih" value={s.laba_bersih} tone={s.laba_bersih >= 0 ? "success" : "danger"} bold />
          <StatBox label="Piutang Aktif" value={s.piutang_aktif} tone="danger" />
          <StatBox label="Total Saldo Apps" value={s.total_saldo_apps} />
        </div>
      )}
    </div>
  );
}

function SharedHarian({ authGet }) {
  const [days, setDays] = useState(14);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    authGet("/daily-breakdown", { days })
      .then((r) => setData(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [days, authGet]);

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 flex-wrap">
        <p className="text-xs uppercase tracking-wide text-muted-foreground font-semibold">Tampilkan</p>
        {[7, 14, 30, 60].map((d) => (
          <Button key={d} data-testid={`shared-days-${d}`} variant={days === d ? "default" : "outline"} size="sm" onClick={() => setDays(d)}>
            {d} hari
          </Button>
        ))}
      </div>
      {loading || !data ? (
        <Skeleton className="h-96 w-full" />
      ) : (
        <Card className="overflow-x-auto">
          <table className="w-full text-sm min-w-[900px]">
            <thead className="bg-muted/40 text-xs uppercase tracking-wide text-muted-foreground border-b">
              <tr>
                <th className="text-left px-3 py-2.5 font-semibold">Tgl</th>
                <th className="text-left px-3 py-2.5 font-semibold">Hari</th>
                <th className="text-right px-3 py-2.5 font-semibold">Modal</th>
                <th className="text-right px-3 py-2.5 font-semibold">Pemasukan</th>
                <th className="text-right px-3 py-2.5 font-semibold">Pengeluaran</th>
                <th className="text-right px-3 py-2.5 font-semibold">Untung</th>
                {data.personal_names.map((n) => (
                  <th key={n} className="text-right px-3 py-2.5 font-semibold">{n}</th>
                ))}
                <th className="text-right px-3 py-2.5 font-semibold">Pinjaman Orang</th>
                <th className="text-right px-3 py-2.5 font-semibold">Sisa Saldo</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {data.days.map((d) => (
                <tr key={d.tanggal} data-testid={`shared-daily-row-${d.tanggal}`} className={d.is_weekend ? "bg-amber-50/70 dark:bg-amber-500/10" : "hover:bg-accent/30"}>
                  <td className="px-3 py-2 whitespace-nowrap font-medium">{d.label || d.tanggal.slice(5)}</td>
                  <td className="px-3 py-2 whitespace-nowrap">{d.hari}</td>
                  <td className="px-3 py-2 text-right money text-muted-foreground">{rupiah(d.modal)}</td>
                  <td className="px-3 py-2 text-right money text-emerald-600 dark:text-emerald-400">{d.pemasukan ? rupiah(d.pemasukan) : "-"}</td>
                  <td className="px-3 py-2 text-right money text-rose-600 dark:text-rose-400">{d.pengeluaran ? rupiah(d.pengeluaran) : "-"}</td>
                  <td className={`px-3 py-2 text-right money font-semibold ${d.untung >= 0 ? "text-emerald-700" : "text-rose-700"}`}>
                    {rupiah(d.untung)}
                  </td>
                  {data.personal_names.map((n) => {
                    if (d.secret) {
                      const v = d.personal_cum?.[n] || 0;
                      return (
                        <td key={n} className="px-3 py-2 text-right money">
                          {v > 0 ? <span className="font-semibold text-primary">{rupiah(v)}</span> : <span className="text-muted-foreground">-</span>}
                        </td>
                      );
                    }
                    const items = d.pinjaman_personal?.[n] || [];
                    const sum = items.reduce((s, x) => s + x.amount, 0);
                    return (
                      <td key={n} className="px-3 py-2 text-right money">
                        {sum > 0 ? <span className="font-semibold text-primary">{rupiah(sum)}</span> : <span className="text-muted-foreground">-</span>}
                      </td>
                    );
                  })}
                  <td className="px-3 py-2 text-right money">
                    {(d.secret ? d.orang_cum : d.total_pinjaman_orang) > 0 ? (
                      <span className="font-semibold text-primary">{rupiah(d.secret ? d.orang_cum : d.total_pinjaman_orang)}</span>
                    ) : (
                      <span className="text-muted-foreground">-</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right money font-semibold">{rupiah(d.sisa_saldo)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}
      <Card className="p-4 bg-muted/40 border-dashed">
        <p className="text-xs text-muted-foreground flex items-start gap-2">
          <Info className="h-3.5 w-3.5 mt-0.5" />
          <span><strong>Formula:</strong> Sisa Saldo = Modal + akumulasi Untung − akumulasi Pinjaman Outstanding.</span>
        </p>
      </Card>
    </div>
  );
}

function SharedRekap({ authGet }) {
  const [period, setPeriod] = useState("month");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    authGet("/rekapitulasi", { period })
      .then((r) => setData(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [period, authGet]);

  if (loading || !data) return <Skeleton className="h-96 w-full" />;

  const rows = [
    { no: 1, item: "Modal", kode: "A", rumus: "", nilai: data.modal, section: "MODAL" },
    { section: "REKAPITULASI" },
    { no: 2, item: "Saldo Terakhir", kode: "B", rumus: "Kas + Saldo App", nilai: data.saldo_terakhir },
    ...Object.entries(data.personal_loans).map(([name, val], i) => ({
      no: 3 + i, item: name, kode: String.fromCharCode(67 + i), rumus: "", nilai: val,
    })),
    { no: 5, item: "Pinjaman Orang", kode: "E", rumus: "", nilai: data.pinjaman_orang },
    { no: 6, item: "Total Saldo", kode: "F", rumus: "B + C + D + E", nilai: data.total_saldo, highlight: "success" },
    { no: 7, item: "Selisih Saldo", kode: "G", rumus: "F - A", nilai: data.selisih_saldo },
    { section: "KEUNTUNGAN" },
    { no: 8, item: "Pemasukan", kode: "H", rumus: "Σ Keuntungan trx", nilai: data.pemasukan },
    { no: 9, item: "Pengeluaran", kode: "I", rumus: "Σ Expenses", nilai: data.pengeluaran },
    { no: 10, item: "Untung", kode: "J", rumus: "H - I", nilai: data.untung, highlight: "success" },
    { no: 11, item: "Selisih Untung", kode: "K", rumus: "J - G", nilai: data.selisih_untung, highlight: "warn" },
  ];

  return (
    <div className="space-y-4">
      <Tabs value={period} onValueChange={setPeriod}>
        <TabsList>
          {PERIODS.map((p) => (
            <TabsTrigger key={p.value} value={p.value} data-testid={`shared-rekap-period-${p.value}`}>{p.label}</TabsTrigger>
          ))}
        </TabsList>
      </Tabs>
      <Card className="overflow-hidden hidden md:block">
        <table className="w-full text-sm">
          <thead className="bg-emerald-600 text-white text-xs uppercase tracking-wide">
            <tr>
              <th className="text-center px-3 py-3 font-bold w-12">NO</th>
              <th className="text-left px-3 py-3 font-bold">ITEM</th>
              <th className="text-center px-3 py-3 font-bold w-16">KODE</th>
              <th className="text-left px-3 py-3 font-bold">RUMUS</th>
              <th className="text-right px-3 py-3 font-bold">NILAI</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {rows.map((r, i) => {
              if (r.section && !r.item) {
                return (
                  <tr key={"sec-" + i} className="bg-muted/60">
                    <td colSpan={5} className="text-center px-3 py-2.5 font-bold text-xs uppercase tracking-widest">{r.section}</td>
                  </tr>
                );
              }
              const bg = r.highlight === "success" ? "bg-emerald-500/10" : r.highlight === "warn" ? "bg-amber-500/10" : "";
              return (
                <tr key={r.kode + i} data-testid={`shared-rekap-row-${r.kode}`} className={`hover:bg-accent/40 ${bg}`}>
                  <td className="text-center px-3 py-2.5 text-muted-foreground">{r.no}</td>
                  <td className="px-3 py-2.5 font-medium">{r.item}</td>
                  <td className="text-center px-3 py-2.5 font-mono text-xs text-muted-foreground">{r.kode}</td>
                  <td className="px-3 py-2.5 font-mono text-xs text-muted-foreground">{r.rumus}</td>
                  <td className={`text-right px-3 py-2.5 money font-semibold ${r.highlight === "success" ? "text-emerald-700 dark:text-emerald-400" : r.highlight === "warn" ? "text-amber-700 dark:text-amber-400" : ""}`}>
                    {rupiah(r.nilai)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </Card>

      {/* Mobile: daftar kartu agar ramah layar kecil */}
      <div className="md:hidden space-y-2.5">
        {rows.map((r, i) => {
          if (r.section && !r.item) {
            return (
              <div key={"m-sec-" + i} className="text-center text-[11px] font-bold uppercase tracking-widest text-muted-foreground pt-2 pb-0.5">
                {r.section}
              </div>
            );
          }
          const tone = r.highlight === "success" ? "border-emerald-500/40 bg-emerald-500/5" : r.highlight === "warn" ? "border-amber-500/40 bg-amber-500/5" : "";
          const valColor = r.highlight === "success" ? "text-emerald-700 dark:text-emerald-400" : r.highlight === "warn" ? "text-amber-700 dark:text-amber-400" : "";
          return (
            <Card key={"m-" + r.kode + i} data-testid={`shared-rekap-m-row-${r.kode}`} className={`p-3.5 flex items-center justify-between gap-3 ${tone}`}>
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-mono text-[10px] px-1.5 py-0.5 rounded bg-muted text-muted-foreground shrink-0">{r.kode}</span>
                  <p className="font-semibold text-sm truncate">{r.item}</p>
                </div>
                {r.rumus && <p className="text-[11px] text-muted-foreground font-mono mt-1">{r.rumus}</p>}
              </div>
              <p className={`money font-bold text-base text-right shrink-0 ${valColor}`}>{rupiah(r.nilai)}</p>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
